import { useEffect, useState, useCallback, useRef } from "react";
import { GameLaunchStatus } from "../constants/webrtc";
import type { StreamStatus } from "..";

interface UseStreamStatusProps {
  streamStatusURL: string;
  pollingInterval?: number;
  onStreamStatus?: (status: StreamStatus) => void;
}

const DEFAULT_POLLING_INTERVAL = 2500; // 2.5 seconds
const TERMINAL_STATUSES = [
  GameLaunchStatus.ERROR,
  GameLaunchStatus.STATUS_NOGAMELICENSE,
  GameLaunchStatus.STATUS_GAMEEXITED,
  GameLaunchStatus.STATUS_GAMELAUNCH_TIMEOUT,
  GameLaunchStatus.ERR_NOTACTIVE,
  GameLaunchStatus.STATUS_UPDATEDETECTED,
] as const;

export const useStreamStatus = ({
  streamStatusURL,
  pollingInterval = DEFAULT_POLLING_INTERVAL,
  onStreamStatus,
}: UseStreamStatusProps) => {
  const [streamStatus, setStreamStatus] = useState<StreamStatus>();
  const cleanupRef = useRef<(() => void) | null>(null);
  const previousStatusRef = useRef<string>();
  const steamLoginSuccessSeenRef = useRef<boolean>(false);

  const stopPolling = useCallback(() => {
    cleanupRef.current?.();
  }, []);

  useEffect(() => {
    if (!streamStatusURL) return;

    previousStatusRef.current = undefined;
    steamLoginSuccessSeenRef.current = false;

    let isSubscribed = true;
    let currentAbortController: AbortController | null = null;

    const fetchStreamStatus = async () => {
      currentAbortController?.abort();
      currentAbortController = new AbortController();
      try {
        const response = await fetch(streamStatusURL, {
          signal: currentAbortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!isSubscribed) return;

        const data = await response.json();
        const newState = {
          status: data.streamstatus || data.status || "",
          message: data.message || "",
        };

        const previousStatus = previousStatusRef.current;
        const currentStatus = newState.status;

        // Track if we've seen explicit Steam login success
        if (currentStatus === GameLaunchStatus.STATUS_STEAMLOGIN_SUCCESS) {
          steamLoginSuccessSeenRef.current = true;
        }

        // If after STREAMLOGIN status we haven't received STEAMLOGIN_SUCCESS
        // but we see a different non-terminal status, assume that login succeeded
        if (
          previousStatus === GameLaunchStatus.STATUS_STEAMLOGIN &&
          currentStatus !== GameLaunchStatus.STATUS_STEAMLOGIN &&
          !TERMINAL_STATUSES.includes(currentStatus) &&
          currentStatus !== GameLaunchStatus.STATUS_STEAMLOGIN_SUCCESS &&
          !steamLoginSuccessSeenRef.current
        ) {
          const successState = {
            status: GameLaunchStatus.STATUS_STEAMLOGIN_SUCCESS,
            message: "Steam login successful (inferred)",
          };
          onStreamStatus?.(successState);
          steamLoginSuccessSeenRef.current = true;
        } else if (currentStatus) {
          onStreamStatus?.(newState);
        }

        previousStatusRef.current = currentStatus;

        setStreamStatus(newState);

        if (TERMINAL_STATUSES.includes(newState.status)) {
          cleanup();
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
        console.error("Failed to fetch stream status:", error);
      }
    };

    // Initial fetch
    void fetchStreamStatus();

    // Set up polling interval
    const interval = setInterval(
      () => void fetchStreamStatus(),
      pollingInterval,
    );

    const cleanup = () => {
      isSubscribed = false;
      clearInterval(interval);
      currentAbortController?.abort();
      cleanupRef.current = null;
    };

    cleanupRef.current = cleanup;

    return () => {
      cleanup();
    };
  }, [streamStatusURL, pollingInterval]);

  return { streamStatus, stopPolling };
};
